import React, { useEffect, useRef, useState } from "react";
import { Pencil, Plus, RefreshCw, Search, Trash2, X } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useMenuHubApi } from "../../api/menuHub";
import AdminShell, { AdminSection } from "../../components/admin-shell";
import AccessTablePagination from "../../components/access-table-pagination";
import UserFormModal from "../../components/user-form-modal";
import {
    MobileUsuariosSkeleton,
    PainelShimmerChrome,
    UsuariosTableSkeleton,
} from "../../components/skeletons";
import SolutionsPanel from "../solutions-panel";
import { useTheme } from "../../theme/useTheme";
import { AdminPagination, AdminUser } from "../../types";
import "./styles.scss";

const AdminPanel: React.FC = () => {
    const api = useMenuHubApi();
    const apiRef = useRef(api);
    apiRef.current = api;

    const location = useLocation();
    const navigate = useNavigate();
    const { isDarkMode } = useTheme();
    const [section, setSection] = useState<AdminSection>("access");
    const [search, setSearch] = useState("");
    const [users, setUsers] = useState<AdminUser[]>([]);
    const [pagination, setPagination] = useState<AdminPagination | null>(null);
    const [page, setPage] = useState(0);
    const [loadingUsers, setLoadingUsers] = useState(true);
    const [reloadKey, setReloadKey] = useState(0);
    const [formMode, setFormMode] = useState<"create" | "edit">("create");
    const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
    const [formOpen, setFormOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState<AdminUser | null>(null);
    const [deleting, setDeleting] = useState(false);
    const [requestError, setRequestError] = useState("");

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const editUser = params.get("editUser");

        if (params.get("newUser") === "true") {
            setFormMode("create");
            setSelectedUser(null);
            setFormOpen(true);
            navigate("/painel-central/admin-panel", { replace: true });
        } else if (editUser) {
            setFormMode("edit");
            setSelectedUser({
                usuario: editUser,
                email: editUser,
                loginTasy: params.get("loginTasy") || "",
            });
            setFormOpen(true);
            navigate("/painel-central/admin-panel", { replace: true });
        }
    }, [location.search, navigate]);

    useEffect(() => {
        if (section !== "access") {
            return;
        }

        let cancelled = false;
        setLoadingUsers(true);
        setRequestError("");

        const loadUsers = async () => {
            try {
                const response = await apiRef.current.getProfile(search, 8, page);
                if (!cancelled) {
                    setUsers(response?.usuarios ?? []);
                    setPagination(response?.pagination ?? null);
                }
            } catch {
                if (!cancelled) {
                    setUsers([]);
                    setPagination(null);
                    setRequestError("Não foi possível carregar os usuários.");
                }
            } finally {
                if (!cancelled) {
                    setLoadingUsers(false);
                }
            }
        };

        loadUsers();

        return () => {
            cancelled = true;
        };
    }, [page, reloadKey, search, section]);

    useEffect(() => {
        if (!deleteTarget || deleting) {
            return;
        }

        const closeOnEscape = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                setDeleteTarget(null);
            }
        };

        document.addEventListener("keydown", closeOnEscape);
        return () => document.removeEventListener("keydown", closeOnEscape);
    }, [deleteTarget, deleting]);

    const openCreateForm = () => {
        setFormMode("create");
        setSelectedUser(null);
        setFormOpen(true);
    };

    const openEditForm = (user: AdminUser) => {
        setFormMode("edit");
        setSelectedUser(user);
        setFormOpen(true);
    };

    const resetSearch = () => {
        setSearch("");
        setPage(0);
    };

    const handleSaved = () => {
        setPage(0);
        setReloadKey((current) => current + 1);
    };

    const confirmDelete = async () => {
        if (!deleteTarget) {
            return;
        }

        setDeleting(true);
        setRequestError("");

        try {
            await apiRef.current.deleteUser(deleteTarget.email);
            setDeleteTarget(null);
            setReloadKey((current) => current + 1);
        } catch {
            setRequestError("Não foi possível excluir o usuário.");
        } finally {
            setDeleting(false);
        }
    };

    const totalPages = Math.max(1, Number(pagination?.totalPages) || 1);
    const currentPage = Math.min(page + 1, totalPages);
    const totalCount = Math.max(0, Number(pagination?.totalCount) || 0);
    const itemsPerPage = Math.max(1, Number(pagination?.totalItemsPerPage) || 8);
    const itemsInPage = Math.max(0, Number(pagination?.itemsInThePage) || users.length);
    const rangeStart = itemsInPage > 0 ? page * itemsPerPage + 1 : 0;
    const rangeEnd = itemsInPage > 0 ? Math.min(rangeStart + itemsInPage - 1, totalCount) : 0;
    const showInitialSkeleton = loadingUsers && users.length === 0 && !pagination;

    return (
        <AdminShell activeSection={section} onSectionChange={setSection}>
            <PainelShimmerChrome isDarkMode={isDarkMode}>
            <div hidden={section !== "access"}>
                    <section className="access-management">
                        <header className="access-management__header">
                            <h1>Gerenciamento de acessos</h1>
                            <button type="button" onClick={openCreateForm}>
                                <Plus size={16} />
                                <span className="access-management__cta-desktop">Novo usuário</span>
                                <span className="access-management__cta-mobile">Novo</span>
                            </button>
                        </header>

                        <div className="access-management__panel">
                            <div className="access-management__toolbar">
                                <div className="access-management__search">
                                    <Search size={16} className="access-management__search-icon" />
                                    <input
                                        type="text"
                                        placeholder="Procurar"
                                        aria-label="Procurar usuários"
                                        value={search}
                                        onChange={(event) => {
                                            setSearch(event.target.value);
                                            setPage(0);
                                        }}
                                    />
                                    {search && (
                                        <button
                                            type="button"
                                            aria-label="Limpar busca"
                                            onClick={() => {
                                                setSearch("");
                                                setPage(0);
                                            }}
                                        >
                                            <X size={14} />
                                        </button>
                                    )}
                                </div>
                                <button type="button" className="access-management__reset" onClick={resetSearch}>
                                    <RefreshCw size={16} />
                                    <span>Redefinir</span>
                                </button>
                            </div>

                            {requestError && (
                                <p className="access-management__error" role="alert">{requestError}</p>
                            )}

                            <div className="access-management__desktop">
                                {showInitialSkeleton ? (
                                    <UsuariosTableSkeleton isDarkMode={isDarkMode} />
                                ) : (
                                    <div className="access-management__table-wrap">
                                        <table>
                                            <thead>
                                                <tr>
                                                    <th>Nome</th>
                                                    <th>E-mail</th>
                                                    <th>Login Tasy</th>
                                                    <th>Ações</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {users.length ? (
                                                    users.map((user, index) => (
                                                        <tr key={user.email} className={index % 2 ? "is-zebra" : ""}>
                                                            <td>{user.usuario}</td>
                                                            <td>{user.email}</td>
                                                            <td>{user.loginTasy || ""}</td>
                                                            <td>
                                                                <div className="access-management__actions">
                                                                    <button
                                                                        type="button"
                                                                        className="access-management__action"
                                                                        aria-label={`Editar ${user.usuario}`}
                                                                        title="Editar"
                                                                        onClick={() => openEditForm(user)}
                                                                    >
                                                                        <Pencil size={16} />
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        className="access-management__action access-management__action--delete"
                                                                        aria-label={`Excluir ${user.usuario}`}
                                                                        title="Excluir"
                                                                        onClick={() => setDeleteTarget(user)}
                                                                    >
                                                                        <Trash2 size={16} />
                                                                    </button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan={4} className="access-management__empty">
                                                            Nenhum usuário encontrado.
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>

                            <div className="access-management__mobile">
                                {showInitialSkeleton ? (
                                    <MobileUsuariosSkeleton isDarkMode={isDarkMode} />
                                ) : users.length ? (
                                    <div className="access-management__cards">
                                        {users.map((user, index) => (
                                            <article key={user.email} className={index % 2 ? "is-zebra" : ""}>
                                                <div className="access-management__card-header">
                                                    <div>
                                                        <h2>{user.usuario}</h2>
                                                        <p>{user.email}</p>
                                                        <span>Login Tasy: {user.loginTasy || ""}</span>
                                                    </div>
                                                    <div className="access-management__actions">
                                                        <button
                                                            type="button"
                                                            className="access-management__action"
                                                            aria-label={`Editar ${user.usuario}`}
                                                            title="Editar"
                                                            onClick={() => openEditForm(user)}
                                                        >
                                                            <Pencil size={16} />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="access-management__action access-management__action--delete"
                                                            aria-label={`Excluir ${user.usuario}`}
                                                            title="Excluir"
                                                            onClick={() => setDeleteTarget(user)}
                                                        >
                                                            <Trash2 size={16} />
                                                        </button>
                                                    </div>
                                                </div>
                                            </article>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="access-management__empty">Nenhum usuário encontrado.</p>
                                )}
                            </div>

                        </div>

                        {!showInitialSkeleton && totalCount > 0 && (
                            <footer className="access-management__pagination">
                                <p>
                                    Mostrando {rangeStart} a {rangeEnd} de {totalCount} registros
                                </p>
                                <AccessTablePagination
                                    currentPage={currentPage}
                                    totalPages={totalPages}
                                    disabled={loadingUsers}
                                    ariaLabel="Paginação de usuários"
                                    hasPreviousPage={currentPage > 1}
                                    hasNextPage={currentPage < totalPages}
                                    onPageChange={(nextPage) => setPage(nextPage - 1)}
                                />
                            </footer>
                        )}
                    </section>
            </div>

            <div hidden={section !== "solutions"}>
                <SolutionsPanel active={section === "solutions"} />
            </div>

            <UserFormModal
                open={formOpen}
                mode={formMode}
                user={selectedUser}
                onClose={() => setFormOpen(false)}
                onSaved={handleSaved}
            />

            {deleteTarget && (
                <div
                    className="access-delete"
                    role="presentation"
                    onMouseDown={deleting ? undefined : () => setDeleteTarget(null)}
                >
                    <section
                        className="access-delete__dialog"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="access-delete-title"
                        aria-describedby="access-delete-description"
                        onMouseDown={(event) => event.stopPropagation()}
                    >
                        <header className="access-delete__header">
                            <h2 id="access-delete-title">Excluir usuário</h2>
                            <button
                                type="button"
                                className="access-delete__close"
                                aria-label="Fechar"
                                onClick={() => setDeleteTarget(null)}
                                disabled={deleting}
                            >
                                <X size={18} />
                            </button>
                        </header>
                        <p id="access-delete-description">
                            Tem certeza que deseja excluir este usuário? Esta ação não pode ser desfeita.
                        </p>
                        <div className="access-delete__actions">
                            <button type="button" onClick={() => setDeleteTarget(null)} disabled={deleting}>
                                Cancelar
                            </button>
                            <button type="button" onClick={confirmDelete} disabled={deleting} autoFocus>
                                {deleting ? "Excluindo..." : "Excluir"}
                            </button>
                        </div>
                    </section>
                </div>
            )}
            </PainelShimmerChrome>
        </AdminShell>
    );
};

export default AdminPanel;
